import type {
    App,
    AppCrm,
    AppCustomerZone,
    AppGridSetting,
    AppointmentPage,
    AppPortal,
    BackOrderPage,
    BasketSettings,
    CatalogSettings,
    FeatureSettings,
    Image,
    InvoicePage,
    NotePage,
    OrderListPage,
    OrderPage,
    QuotePage,
    ShopPolicy,
    TenantInfo,
    TenantSettings
} from "@/shared/graphql/types";
import {DiscountSettings, FavIcon} from "@/shared/graphql/types";

export interface AppFeaturesSpec {
    isMultiCustomer: boolean
}

function isAppCrm(app: App): app is AppCrm {
    return (app as AppCrm).__typename === 'AppCrm';
}

function isAppPortal(app: App): app is AppPortal {
    return (app as AppPortal).__typename === 'AppPortal';
}

function isAppCuz(app: App): app is AppCrm {
    return (app as AppCustomerZone).__typename === 'AppCustomerZone';
}


export class AppSettings {
    readonly colors: AppColors
    readonly images: AppImages
    readonly discountSettings: DiscountSettings
    readonly catalogSettings: CatalogSettings
    readonly basketSettings: BasketSettings
    readonly gridSettings: AppGridSetting[]
    readonly featureSettings: FeatureSettings
    readonly favIcons: FavIcon[]
    private readonly app: App

    constructor(app: App) {
        this.app = app
        this.colors = new AppColors(app)
        this.images = new AppImages(app)
        this.favIcons = app.favIcons
        this.discountSettings = isAppCrm(app) ? app.discountSettings : null
        this.catalogSettings = isAppCrm(app) || isAppPortal(app) ? app.catalogSettings : null
        this.basketSettings = isAppCrm(app) || isAppPortal(app) || isAppCuz(app) ? app.basketSettings : null
        this.gridSettings = isAppCrm(app) || isAppPortal(app) || isAppCuz(app) ? app.gridSettings : null
        this.featureSettings = app.featureSettings
    }

    hasFeature(name: String): Boolean {
        return this.app.features.some((f) => f.name == name)
    }
}

export class AppColors {
    readonly primary: String
    readonly primaryText: String
    readonly primaryDarker: String
    readonly primaryAlphaZero: String
    readonly primaryAlphaLight: String
    readonly primaryAlphaTransparent: String
    readonly secondary: String
    readonly backgroundColor: String
    readonly header: String
    readonly text: String
    readonly headerText: String
    readonly headerTextHover: String
    readonly link: String
    readonly linkHover: String
    readonly sidebar: String
    readonly sidebarText: String
    readonly sidebarHover: String

    constructor(app: App) {
        //Todo defaults assignen indien waarde null is
        this.primary = app.colorScheme.colors.find(color => color.name == "primary")?.hsl
        this.primaryText = app.colorScheme.colors.find(color => color.name == "primary_text")?.hsl
        this.sidebar = app.colorScheme.colors.find(color => color.name == "sidebar")?.hsl
        this.sidebarText = app.colorScheme.colors.find(color => color.name == "sidebar_text")?.hsl
        this.sidebarHover = app.colorScheme.colors.find(color => color.name == "sidebar_hover")?.hsl
        this.secondary = app.colorScheme.colors.find(color => color.name == "secondary")?.value
        this.text = app.colorScheme.colors.find(color => color.name == "text")?.value
        this.backgroundColor = app.colorScheme.colors.find(color => color.name == "background-color")?.value
        this.header = app.colorScheme.colors.find(color => color.name == "header")?.value
        this.headerText = app.colorScheme.colors.find(color => color.name == "header_text")?.value
        this.headerTextHover = app.colorScheme.colors.find(color => color.name == "header_text_hover")?.value
        this.link = app.colorScheme.colors.find(color => color.name == "link")?.value
        this.linkHover = app.colorScheme.colors.find(color => color.name == "link_hover")?.value
        //@ts-ignore
        this.primaryAlphaZero = app.colorScheme.primaryAlphaZero.rgba
        //@ts-ignore
        this.primaryAlphaLight = app.colorScheme.primaryAlphaLight.rgba
        //@ts-ignore
        this.primaryDarker = app.colorScheme.primaryDarker.darker
        //@ts-ignore
        this.primaryAlphaTransparent = app.colorScheme.primaryAlphaTransparent.rgba
    }
}

export class AppImages {
    readonly logo: Image

    constructor(app: App) {
        this.logo = app.images.find(image => image.name == "logo").image
    }
}

export class TenantData {
    readonly name: String
    readonly tenantInfo: TenantInfo
    readonly tenantSettings: TenantSettings
    readonly applicationSettings: AppSettings
    readonly termsOfService: ShopPolicy

    constructor(name: String, tenantInfo: TenantInfo, tenantSettings: TenantSettings, applicationSettings: AppSettings, termsOfService: ShopPolicy) {
        this.name = name;
        this.tenantInfo = tenantInfo;
        this.tenantSettings = tenantSettings;
        this.applicationSettings = applicationSettings;
        this.termsOfService = termsOfService;
    }
}

export class RecentData {
    readonly orders: OrderPage
    readonly quotes: QuotePage
    readonly orderLists: OrderListPage
    readonly invoices: InvoicePage
    readonly backOrders: BackOrderPage
    readonly appointments: AppointmentPage
    readonly notes: NotePage

    constructor(orders: OrderPage, quotes: QuotePage, orderLists: OrderListPage, invoices: InvoicePage, backOrders: BackOrderPage, notes: NotePage, appointments: AppointmentPage) {
        this.orders = orders;
        this.quotes = quotes;
        this.orderLists = orderLists;
        this.invoices = invoices;
        this.backOrders = backOrders;
        this.notes = notes;
        this.appointments = appointments;
    }
}

export enum MutationMode {
    UPDATE, CREATE
}


export enum AuthView {
    Login = 'Login',
    PasswordReset = 'PasswordReset'
}
